// Tracks "have I seen anything new in my Feed since last visit" — drives the
// fuchsia dot on the Feed tab in Nav.vue and the PWA app-badge count. Per-device
// by design (mocks/notifications-screen.html).
//
// Sources of truth:
//   - `localStorage.scrawl_feed_last_seen_<username>` = epoch ms of last visit
//     to /feed. Initial unseen-count query at app boot uses this.
//   - Module-scoped `unseenCount` = current in-session count, bumped by the
//     LiveQuery handler in App.vue and reset by markFeedSeen.
// Both Nav.vue (binary dot) and App.vue (badge count) read from the same source.

import Parse from "parse";

let unseenCount = 0;

function key(username) {
  return `scrawl_feed_last_seen_${username}`;
}

export function getLastFeedVisitMs(username) {
  if (!username) return 0;
  try {
    const raw = localStorage.getItem(key(username));
    return raw ? parseInt(raw, 10) || 0 : 0;
  } catch {
    return 0;
  }
}

export function getUnseenCount() {
  return unseenCount;
}

export function setUnseenCount(n) {
  unseenCount = Math.max(0, n | 0);
  if (unseenCount > 0) {
    window.dispatchEvent(
      new CustomEvent("scrawl-feed-has-new", { detail: { count: unseenCount } })
    );
  }
}

export function markFeedSeen(username) {
  if (!username) return;
  try {
    localStorage.setItem(key(username), String(Date.now()));
  } catch {
    /* localStorage disabled — degrade silently */
  }
  unseenCount = 0;
  window.dispatchEvent(new CustomEvent("scrawl-feed-seen"));
}

// Called when a new Inbox arrival is detected (LiveQuery in App.vue). The
// caller has already determined the row is for the current user and newer
// than `lastSeen`. We bump the count and emit the event so Nav.vue can light
// the dot and App.vue can update the PWA badge.
export function markFeedHasNew() {
  unseenCount += 1;
  window.dispatchEvent(
    new CustomEvent("scrawl-feed-has-new", { detail: { count: unseenCount } })
  );
}

// One-shot count of "unseen Inbox rows for this user" — used at app boot to
// initialise the dot from server state (covers the case where pushes arrived
// while the app was closed).
export async function fetchUnseenInboxCount(username) {
  if (!username) return 0;
  const lastSeen = getLastFeedVisitMs(username);
  const Inbox = Parse.Object.extend("Inbox");
  const q = new Parse.Query(Inbox);
  q.equalTo("toUser", username);
  if (lastSeen > 0) {
    q.greaterThan("createdAt", new Date(lastSeen));
  }
  try {
    return await q.count();
  } catch {
    return 0;
  }
}
